// src/pages/Dashboard.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, onSnapshot, orderBy, where, Timestamp, limit, doc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
    FaHome,
    FaCheckCircle,
    FaHome as FaHomeEmpty,
    FaUsers,
    FaDollarSign,
    FaExclamationTriangle,
    FaChartLine,
    FaCar,
    FaPaw,
    FaBell,
    FaFileInvoiceDollar,
    FaBullhorn,
    FaDownload,
    FaCalendarAlt,
    FaEye,
    FaHandshake
} from 'react-icons/fa';
import NotificationsCenterModal from '../components/NotificationsCenterModal';
import WelcomeConfigModal from '../components/WelcomeConfigModal';
import NotificationBell from '../components/NotificationBell';
import { calcularEstadoMoroso, useConfiguracionFinanciera, calcularAdeudoConInteres, esCasaCaseta } from '../utils/morosoUtils';
import './Dashboard.css';

const MAX_DASHBOARD_NOTIFICATIONS = 8;
const DASHBOARD_NOTIFICATION_FETCH_WINDOW = 50;
const PET_POSITIVE_RESPONSES = new Set(['si', 'sí', 'true', '1', 'con mascotas', 'mascotas', 'yes']);
const PET_NEGATIVE_RESPONSES = new Set(['no', 'false', '0', 'sin mascotas', 'sin mascota', 'sin', 'ninguna', 'ninguno', 'na', 'n/a']);

const parseNonNegativeNumber = (value) => {
    if (value === null || value === undefined) {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) && value >= 0 ? value : 0;
    }
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    if (typeof value === 'string') {
        const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '.').trim();
        if (!cleaned) {
            return 0;
        }
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};

const hasRegisteredPets = (value) => {
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) && value > 0;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return false;
    }
    const normalized = trimmed.toLowerCase();
    if (PET_POSITIVE_RESPONSES.has(normalized)) {
        return true;
    }
    if (PET_NEGATIVE_RESPONSES.has(normalized)) {
        return false;
    }
    return true;
};

const SUPERADMIN_ALLOWED_CATEGORY_KEYS = [
    'Amenidades',
    'Propuestas',
    'Vigilancia',
    'Votaciones',
    'Anuncios Generales',
    'General',
    'Avisos',
    'Incidencias',
    'Eventos',
    'Citas',
    'Paqueteria',
    'Visitas Frecuentes',
    'Solicitudes y Permisos',
    'Administración',
    'Finanzas',
    'Configuraciones',
    'Comunidad',
];

const COMMUNITY_ALLOWED_ACTOR_ROLES = new Set(['residente', 'superadmin']);

const COMMUNITY_CATEGORY_KEYS = [
    'General',
    'Avisos',
    'Votaciones',
    'Propuestas',
    'Incidencias',
    'Eventos',
    'Citas',
    'Agenda',
    'Solicitudes y Permisos',
    'Validación de Documentos',
    'Validacion de Documentos',
    'Actualizar Datos de Contacto',
    'Datos de Contacto',
    'Finanzas',
    'Paqueteria',
    'Administración',
    'Configuraciones',
    'Comunidad',
    ...SUPERADMIN_ALLOWED_CATEGORY_KEYS,
];

const normalizeCategory = (value) => {
    if (!value) {
        return 'general';
    }
    const base = String(value).toLowerCase().trim();
    try {
        return base
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    } catch (error) {
        return base;
    }
};

const normalizeActorRole = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const COMMUNITY_CATEGORY_SET = new Set(COMMUNITY_CATEGORY_KEYS.map(normalizeCategory));
const SUPERADMIN_CATEGORY_SET = new Set(SUPERADMIN_ALLOWED_CATEGORY_KEYS.map(normalizeCategory));

const Dashboard = () => {
    const navigate = useNavigate();
    // --- ESTADOS ---
    const [casasSummary, setCasasSummary] = useState(null);
    const [financialSummary, setFinancialSummary] = useState(null);
    const [notificaciones, setNotificaciones] = useState([]);
    const [ingresosMes, setIngresosMes] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [adeudoPersonal, setAdeudoPersonal] = useState(0); // Adeudo personal del residente
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    const { userData, fraccionamientoId: activeFraccId } = useAuth();
    const fraccionamientoId = activeFraccId || userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const normalizedRole = typeof userData?.rol === 'string' ? userData.rol.trim().toLowerCase() : null;
    const isPlatformAdmin = normalizedRole === 'plataforma' || normalizedRole === 'platformadmin';
    const isSuperAdmin = normalizedRole === 'superadmin';
    const canViewNotifications = isPlatformAdmin || isSuperAdmin;
    const shouldRestrictActorRoles = !isPlatformAdmin;
    const allowedActorRoleSet = shouldRestrictActorRoles ? COMMUNITY_ALLOWED_ACTOR_ROLES : null;

    // Obtener configuración financiera para cálculo de morosos
    const financialConfig = useConfiguracionFinanciera(fraccionamientoId);

    const cardCopy = useMemo(() => (
        isPlatformAdmin
            ? {
                title: 'Movimientos recientes',
                empty: 'No hay movimientos registrados.',
            }
            : {
                title: 'Actividad Reciente',
                empty: 'Sin actividad reciente.',
            }
    ), [isPlatformAdmin]);

    const notificationsCardTitle = cardCopy.title;
    const emptyNotificationsMessage = cardCopy.empty;

    const notificationCategorySet = useMemo(() => {
        if (isPlatformAdmin) {
            return null;
        }
        if (isSuperAdmin) {
            return SUPERADMIN_CATEGORY_SET;
        }
        return COMMUNITY_CATEGORY_SET;
    }, [isPlatformAdmin, isSuperAdmin]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setCasasSummary(null);
            setFinancialSummary(null);
            setIngresosMes(0);
            setLoading(false);
            return;
        }

        setLoading(true);

        const unsubscribers = [];
        const loadState = { casas: false, ingresos: false };

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const unsubscribeCasas = onSnapshot(casasRef, (snapshot) => {
            const listaCasas = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));

            // Si es un residente (no admin), obtener su adeudo personal
            if (!isSuperAdmin && !isPlatformAdmin && userData) {
                const casaId = userData?.casaId || userData?.casaID || userData?.idCasa;
                const numeroCasa = userData?.numeroCasa || userData?.casaNumero || userData?.numero;

                // Buscar la casa del residente
                let casaResidente = null;
                if (casaId) {
                    casaResidente = listaCasas.find(casa => casa.id === casaId);
                } else if (numeroCasa) {
                    casaResidente = listaCasas.find(casa =>
                        String(casa.numero || '') === String(numeroCasa) ||
                        String(casa.numeroCasa || '') === String(numeroCasa)
                    );
                }

                // Si se encontró la casa, obtener el adeudo real con interés
                if (casaResidente) {
                    const adeudoReal = calcularAdeudoConInteres(casaResidente);
                    setAdeudoPersonal(adeudoReal);
                    console.log('✅ Dashboard: Adeudo personal del residente cargado con interés', {
                        numeroCasa: casaResidente.numero || numeroCasa,
                        adeudoReal: adeudoReal
                    });
                } else {
                    setAdeudoPersonal(0);
                    console.warn('⚠️ Dashboard: No se encontró la casa del residente', { casaId, numeroCasa });
                }
            } else {
                // Si es admin, no hay adeudo personal
                setAdeudoPersonal(0);
            }

            // Filtrar casas excluyendo caseta
            const casasSinCaseta = listaCasas.filter((item) => !esCasaCaseta(item));

            const total = casasSinCaseta.length;
            const disponibles = casasSinCaseta.filter((item) => item.disponible === true).length;
            const ocupadas = total - disponibles;

            // Contar casas morosas usando nueva lógica de umbral
            const morosos = casasSinCaseta.filter((item) => {
                const adeudo = calcularAdeudoConInteres(item);
                const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
                const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);
                return calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
            }).length;

            const adeudoTotal = casasSinCaseta.reduce((sum, item) => {
                const monto = calcularAdeudoConInteres(item);
                return Number.isFinite(monto) ? sum + monto : sum;
            }, 0);

            const morososSinConvenio = casasSinCaseta.filter((item) => {
                const adeudo = calcularAdeudoConInteres(item);
                const sinConvenio = item.convenioActivo !== true;
                const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
                const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);
                return calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos) && sinConvenio;
            }).length;
            const totalResidentes = casasSinCaseta.reduce((sum, item) => (
                sum + parseNonNegativeNumber(
                    item.num_residentes
                    ?? item.numero_residentes
                    ?? item.residentes
                    ?? item.cantidad_residentes
                    ?? item.numResidentes
                    ?? item.habitantes
                    ?? item.Habitantes
                    ?? item.num_habitantes
                    ?? item.cantidad_habitantes
                    ?? item.totalResidentes
                    ?? item.cant_residentes
                    ?? 0,
                )
            ), 0);
            const totalVehiculos = listaCasas.reduce((sum, item) => (
                sum + parseNonNegativeNumber(
                    item.num_vehiculos
                    ?? item.numero_vehiculos
                    ?? item.vehiculos
                    ?? item.Vehiculos
                    ?? item.cantidad_vehiculos
                    ?? item.numVehiculos
                    ?? item.auto
                    ?? item.autos
                    ?? item.cant_vehiculos
                    ?? item.totalVehiculos
                    ?? 0,
                )
            ), 0);
            const casasConMascotas = listaCasas.reduce((count, item) => (
                hasRegisteredPets(
                    item.mascotas
                    ?? item.Mascotas
                    ?? item.tieneMascotas
                    ?? item.detalle_mascotas
                    ?? item.mascota
                    ?? item.tienemascotas
                    ?? item.conmascotas
                    ?? item.mascota_detalle
                    ?? item.tiene_mascotas
                    ?? item.mascota_tipo
                    ?? null,
                ) ? count + 1 : count
            ), 0);

            setCasasSummary({ total, ocupadas, disponibles, morosos, morososSinConvenio, adeudoTotal, totalResidentes, totalVehiculos, casasConMascotas });
            loadState.casas = true;
            if (loadState.ingresos) {
                setLoading(false);
            }
        }, (error) => {
            console.error('Dashboard: error al escuchar casas', error);
            setCasasSummary(null);
            setAdeudoPersonal(0);
            loadState.casas = true;
            if (loadState.ingresos) {
                setLoading(false);
            }
        });
        unsubscribers.push(unsubscribeCasas);

        const inicioMes = new Date();
        inicioMes.setHours(0, 0, 0, 0);
        inicioMes.setDate(1);
        const inicioMesTimestamp = Timestamp.fromDate(inicioMes);

        const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
        const ingresosQuery = query(ingresosRef, where('fecha', '>=', inicioMesTimestamp), orderBy('fecha', 'desc'));
        const unsubscribeIngresos = onSnapshot(ingresosQuery, (snapshot) => {
            const totalIngresos = snapshot.docs.reduce((sum, docSnap) => {
                const data = docSnap.data() || {};
                if (data.deleted) return sum;
                const concepto = String(data.concepto || data.descripcion || '').toLowerCase();

                // Excluir cargos automáticos del sistema, marcadores de deuda y cuotas no cobradas
                const metodoPago = String(data.metodoPago || '').toLowerCase();
                const tipo = String(data.tipo || '').toLowerCase();

                if (metodoPago === 'registro_automatico' ||
                    concepto.includes('adeudo') ||
                    concepto.includes('sin adeudo') ||
                    concepto.includes('saldo final de mes') ||
                    concepto.includes('recargo por morosidad') ||
                    data.esMarcadoHistorial === true ||
                    data.esSoloSaldoFinal === true ||
                    tipo === 'saldo') {
                    return sum;
                }

                const monto = Number(data.montoNeto ?? data.monto) || 0;
                if (monto <= 0) return sum;
                return sum + monto;
            }, 0);
            setIngresosMes(totalIngresos);
            loadState.ingresos = true;
            if (loadState.casas) {
                setLoading(false);
            }
        }, (error) => {
            console.error('Dashboard: error al escuchar ingresos', error);
            setIngresosMes(0);
            loadState.ingresos = true;
            if (loadState.casas) {
                setLoading(false);
            }
        });
        unsubscribers.push(unsubscribeIngresos);

        return () => unsubscribers.forEach((unsub) => {
            if (typeof unsub === 'function') {
                unsub();
            }
        });
    }, [fraccionamientoId, financialConfig]);

    // --- EFECTO PARA NOTIFICACIONES Y VISITAS ---
    useEffect(() => {
        if (!fraccionamientoId || !canViewNotifications) {
            setNotificaciones([]);
            return;
        }

        let rawNotifications = [];
        let visitasData = [];

        // Listener para notificaciones
        const notificacionesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones');
        const q = query(notificacionesRef, orderBy('fecha', 'desc'), limit(DASHBOARD_NOTIFICATION_FETCH_WINDOW));

        const unsubNotificaciones = onSnapshot(q, (snapshot) => {
            rawNotifications = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            combineAndSetNotifications();
        }, (err) => {
            console.error('Dashboard: error en listener notificaciones:', err);
            rawNotifications = [];
            combineAndSetNotifications();
        });

        // Listener para visitas (entradas y salidas)
        const visitasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'accesosVisitantes');
        const unsubVisitas = onSnapshot(visitasRef, (snapshot) => {
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            const hoyTimestamp = Timestamp.fromDate(hoy);

            visitasData = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                const visitanteNombre = data.visitanteNombre || data.visitante || 'Visitante';
                const departamento = data.departamento || data.numeroCasa || '';
                const estadoAcceso = String(data.estadoAcceso || '').toLowerCase();

                // Obtener fecha de entrada o salida
                let fechaTimestamp = null;
                let fechaIso = null;

                if (estadoAcceso === 'dentro') {
                    // Entrada
                    fechaTimestamp = data.entradaRegistradaEn;
                    fechaIso = data.entradaRegistradaIso;
                } else if (estadoAcceso === 'fuera') {
                    // Salida
                    fechaTimestamp = data.salidaRegistradaEn;
                    fechaIso = data.salidaRegistradaIso;
                }

                // Si no hay fecha específica, usar entrada como fallback
                if (!fechaTimestamp && !fechaIso) {
                    fechaTimestamp = data.entradaRegistradaEn;
                    fechaIso = data.entradaRegistradaIso;
                }

                // Convertir a Timestamp si es necesario
                let fecha = null;
                if (fechaTimestamp && typeof fechaTimestamp.toDate === 'function') {
                    fecha = fechaTimestamp;
                } else if (typeof fechaIso === 'string' && fechaIso) {
                    fecha = Timestamp.fromDate(new Date(fechaIso));
                } else if (data.fechaEntrada && typeof data.fechaEntrada.toDate === 'function') {
                    fecha = data.fechaEntrada;
                }

                // Solo incluir visitas de hoy
                if (fecha && fecha.toMillis() >= hoyTimestamp.toMillis()) {
                    const esEntrada = estadoAcceso === 'dentro';
                    visitasData.push({
                        id: `visita_${doc.id}`,
                        titulo: esEntrada
                            ? `Entrada de visitante: ${visitanteNombre}`
                            : `Salida de visitante: ${visitanteNombre}`,
                        descripcion: esEntrada
                            ? `Visitante ingresó al fraccionamiento${departamento ? ` - ${departamento}` : ''}`
                            : `Visitante salió del fraccionamiento${departamento ? ` - ${departamento}` : ''}`,
                        fecha: fecha,
                        categoria: 'Vigilancia',
                        tipoMovimiento: esEntrada ? 'entrada_visitante' : 'salida_visitante',
                        origen: 'vigilancia',
                    });
                }
            });
            combineAndSetNotifications();
        }, (err) => {
            console.error('Dashboard: error en listener visitas:', err);
            visitasData = [];
            combineAndSetNotifications();
        });

        // Función para combinar notificaciones y visitas
        const combineAndSetNotifications = () => {
            let combined = [...rawNotifications];

            // Filtrar notificaciones según permisos
            if (userData?.uid && !isPlatformAdmin && !isSuperAdmin) {
                combined = combined.filter((item) => {
                    return !item.destinatarioUid || item.destinatarioUid === userData.uid;
                });
            }

            // Filtrar por categoría y rol
            combined = combined.filter((item) => {
                if (notificationCategorySet && !notificationCategorySet.has(normalizeCategory(item.categoria))) {
                    return false;
                }
                if (allowedActorRoleSet && !allowedActorRoleSet.has(normalizeActorRole(item.actorRol))) {
                    return false;
                }
                return true;
            });

            // Agregar visitas
            combined = [...combined, ...visitasData];

            // Ordenar por fecha (más reciente primero)
            combined.sort((a, b) => {
                const fechaA = a.fecha?.toMillis ? a.fecha.toMillis() : (a.fecha?.seconds ? a.fecha.seconds * 1000 : 0);
                const fechaB = b.fecha?.toMillis ? b.fecha.toMillis() : (b.fecha?.seconds ? b.fecha.seconds * 1000 : 0);
                return fechaB - fechaA;
            });

            // Limitar a MAX_DASHBOARD_NOTIFICATIONS
            setNotificaciones(combined.slice(0, MAX_DASHBOARD_NOTIFICATIONS));
        };

        return () => {
            unsubNotificaciones();
            unsubVisitas();
        };
    }, [fraccionamientoId, canViewNotifications, notificationCategorySet, allowedActorRoleSet, userData, isPlatformAdmin, isSuperAdmin]);

    // --- EFECTO PARA VALIDAR CONFIGURACIÓN Y MOSTRAR MODAL DE BIENVENIDA ---
    useEffect(() => {
        if (!fraccionamientoId || !isSuperAdmin) {
            return;
        }

        const validateConfig = async () => {
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const unsubConfig = onSnapshot(fraccRef, (snap) => {
                    if (!snap.exists()) return;

                    const data = snap.data();
                    const cfg = data.configuracionFinanciera || {};

                    const cuotaOk = cfg.montoMensual != null && Number(cfg.montoMensual) > 0;
                    const corteOk = cfg.corteDia != null && cfg.corteDia !== '';
                    const sancionOk = cfg.sancionesInicioDia != null && cfg.sancionesInicioDia !== '';

                    const allComplete = cuotaOk && corteOk && sancionOk;

                    if (!allComplete) {
                        setShowWelcomeModal(true);
                    } else {
                        setShowWelcomeModal(false);
                    }
                });

                return unsubConfig;
            } catch (error) {
                console.error('Dashboard: error al validar config financiera', error);
            }
        };

        const unsubscribe = validateConfig();
        return () => {
            if (unsubscribe && typeof unsubscribe.then === 'function') {
                unsubscribe.then(unsub => unsub && unsub());
            }
        };
    }, [fraccionamientoId, isSuperAdmin]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setFinancialSummary(null);
            return undefined;
        }

        const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsubscribe = onSnapshot(fraccRef, (snapshot) => {
            if (!snapshot.exists()) {
                setFinancialSummary(null);
                return;
            }

            const data = snapshot.data() || {};
            const resumenFinanciero = data.resumenFinanciero || data.financialSummary || null;
            if (!resumenFinanciero || typeof resumenFinanciero !== 'object') {
                setFinancialSummary(null);
                return;
            }

            setFinancialSummary({
                totalResidentes: parseNonNegativeNumber(
                    resumenFinanciero.totalResidentes
                    ?? resumenFinanciero.total_residentes
                    ?? 0,
                ),
                totalVehiculos: parseNonNegativeNumber(
                    resumenFinanciero.totalVehiculos
                    ?? resumenFinanciero.total_vehiculos
                    ?? 0,
                ),
                casasConMascotas: parseNonNegativeNumber(
                    resumenFinanciero.casasConMascotas
                    ?? resumenFinanciero.casas_con_mascotas
                    ?? 0,
                ),
            });
        }, (error) => {
            console.error('Dashboard: error al leer resumenFinanciero', error);
            setFinancialSummary(null);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    const resumen = useMemo(() => {
        if (!casasSummary && !financialSummary) {
            return null;
        }

        const base = casasSummary || {};
        const fin = financialSummary || {};

        return {
            total: base.total ?? 0,
            ocupadas: base.ocupadas ?? 0,
            disponibles: base.disponibles ?? 0,
            morosos: base.morosos ?? 0,
            morososSinConvenio: base.morososSinConvenio ?? 0,
            adeudoTotal: base.adeudoTotal ?? 0,
            totalResidentes: base.totalResidentes ?? fin.totalResidentes ?? 0,
            totalVehiculos: base.totalVehiculos ?? fin.totalVehiculos ?? 0,
            casasConMascotas: base.casasConMascotas ?? fin.casasConMascotas ?? 0,
        };
    }, [casasSummary, financialSummary]);

    const porcentajeSinConvenio = useMemo(() => {
        if (!resumen || resumen.morosos === 0) return 0;
        return (resumen.morososSinConvenio / resumen.morosos) * 100;
    }, [resumen]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
    };

    const toJsDate = (value) => {
        if (!value) {
            return null;
        }
        if (typeof value.toDate === 'function') {
            try {
                return value.toDate();
            } catch (error) {
                return null;
            }
        }
        if (value instanceof Date) {
            return value;
        }
        if (typeof value === 'number') {
            return new Date(value);
        }
        if (typeof value === 'string') {
            const parsed = Date.parse(value);
            return Number.isNaN(parsed) ? null : new Date(parsed);
        }
        return null;
    };

    // Calcular tasa de cobro (casas al corriente / total casas, excluyendo caseta)
    const tasaCobro = useMemo(() => {
        if (!resumen || resumen.total === 0) return 0;
        // Casas al corriente = total - morosos
        const casasAlCorriente = resumen.total - resumen.morosos;
        return Math.round((casasAlCorriente / resumen.total) * 100);
    }, [resumen]);

    // --- JSX DE RETORNO ---
    return (
        <div className="dashboard-wrapper">
            {/* Contenido principal */}
            <div className="dashboard-main">
                {/* Header con título y descripción */}
                <header className="dashboard-header">
                    <div className="dashboard-header-content">
                        <div className="dashboard-header-icon">
                            <FaChartLine />
                        </div>
                        <div className="dashboard-header-text">
                            <h1>Dashboard</h1>
                            <p>Vista general del fraccionamiento, métricas principales y actividad reciente</p>
                        </div>
                    </div>
                </header>

                {/* Tarjetas de métricas principales */}
                <div className="dashboard-metrics-grid">
                    <div className="metric-card">
                        <div className="metric-icon metric-icon-blue">
                            <FaHome />
                        </div>
                        <div className="metric-content">
                            <p className="metric-label">Total de casas</p>
                            <p className="metric-value">{loading ? '...' : (resumen?.total ?? 0)}</p>
                        </div>
                    </div>

                    <div className="metric-card">
                        <div className="metric-icon metric-icon-green">
                            <FaCheckCircle />
                        </div>
                        <div className="metric-content">
                            <p className="metric-label">Ocupadas</p>
                            <p className="metric-value">{loading ? '...' : (resumen?.ocupadas ?? 0)}</p>
                        </div>
                    </div>

                    <div className="metric-card">
                        <div className="metric-icon metric-icon-gray">
                            <FaHomeEmpty />
                        </div>
                        <div className="metric-content">
                            <p className="metric-label">Disponibles</p>
                            <p className="metric-value">{loading ? '...' : (resumen?.disponibles ?? 0)}</p>
                        </div>
                    </div>

                    <div className="metric-card">
                        <div className="metric-icon metric-icon-purple">
                            <FaUsers />
                        </div>
                        <div className="metric-content">
                            <p className="metric-label">Habitantes</p>
                            <p className="metric-value">{loading ? '...' : (resumen?.totalResidentes ?? 0)}</p>
                        </div>
                    </div>
                </div>

                {/* Sección financiera y Actividad Reciente */}
                <div className="dashboard-bottom-grid">
                    {/* Sección financiera destacada - Vertical */}
                    <div className="dashboard-financial-section">
                        <h2 className="financial-section-title">Estado Financiero</h2>
                        <p className="financial-section-subtitle">Resumen de ingresos y pagos pendientes</p>

                        <div className="financial-cards-vertical">
                            <div className="financial-card financial-card-income">
                                <div className="financial-card-icon">
                                    <FaDollarSign />
                                </div>
                                <div className="financial-card-content">
                                    <p className="financial-card-label">Ingresos del mes</p>
                                    <p className="financial-card-value">{formatCurrency(ingresosMes)}</p>
                                </div>
                            </div>

                            <div className="financial-card financial-card-debt">
                                <div className="financial-card-icon">
                                    <FaExclamationTriangle />
                                </div>
                                <div className="financial-card-content">
                                    <p className="financial-card-label">
                                        {!isSuperAdmin && !isPlatformAdmin ? 'Mi adeudo actual' : 'Adeudos pendientes'}
                                    </p>
                                    <p className="financial-card-value">
                                        {formatCurrency((!isSuperAdmin && !isPlatformAdmin) ? adeudoPersonal : (resumen?.adeudoTotal ?? 0))}
                                    </p>
                                </div>
                            </div>

                            {(isSuperAdmin || isPlatformAdmin) && (
                                <div className="financial-card financial-card-no-agreement">
                                    <div className="financial-card-icon">
                                        <FaHandshake />
                                    </div>
                                    <div className="financial-card-content">
                                        <p className="financial-card-label">Deudores sin convenio</p>
                                        <p className="financial-card-value">{porcentajeSinConvenio.toFixed(1)}%</p>
                                    </div>
                                </div>
                            )}

                            <div className="financial-card financial-card-residents">
                                <div className="financial-card-icon">
                                    <FaUsers />
                                </div>
                                <div className="financial-card-content">
                                    <p className="financial-card-label">Residentes con adeudos</p>
                                    <p className="financial-card-value">{resumen?.morosos ?? 0}</p>
                                </div>
                            </div>

                            <div className="financial-card financial-card-rate">
                                <div className="financial-card-icon">
                                    <FaChartLine />
                                </div>
                                <div className="financial-card-content">
                                    <p className="financial-card-label">Tasa de cobro</p>
                                    <div className="rate-progress-container">
                                        <div className="rate-progress-bar">
                                            <div
                                                className="rate-progress-fill"
                                                style={{ width: `${tasaCobro}%` }}
                                            ></div>
                                        </div>
                                        <p className="rate-progress-text">{tasaCobro.toFixed(1)}%</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Panel de Actividad Reciente */}
                    <div className="dashboard-activity-card">
                        <div className="activity-card-header">
                            <h3>{notificationsCardTitle}</h3>
                            {canViewNotifications && notificaciones.length > 0 && (
                                <button
                                    type="button"
                                    className="activity-see-all-btn"
                                    onClick={() => setShowNotificationsModal(true)}
                                >
                                    Ver todo
                                </button>
                            )}
                        </div>
                        <div className="activity-card-content">
                            {canViewNotifications ? (
                                notificaciones.length > 0 ? (
                                    <ul className="activity-list">
                                        {notificaciones.slice(0, 4).map(noti => {
                                            const date = toJsDate(noti.fecha);
                                            return (
                                                <li key={noti.id} className="activity-item">
                                                    <div className="activity-item-content">
                                                        <strong className="activity-item-title">{noti.titulo || 'Notificación'}</strong>
                                                        <p className="activity-item-description">{noti.descripcion || 'Sin descripción.'}</p>
                                                        <small className="activity-item-time">
                                                            {date ? date.toLocaleString('es-MX', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            }) : 'Sin fecha'}
                                                        </small>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <div className="activity-empty">
                                        <FaBell className="activity-empty-icon" />
                                        <p>{emptyNotificationsMessage}</p>
                                        <p className="activity-empty-subtitle">Las notificaciones aparecerán aquí</p>
                                    </div>
                                )
                            ) : (
                                <div className="activity-empty">
                                    <p>Las notificaciones globales solo están disponibles para el equipo de plataforma.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Métricas adicionales: Vehículos y Mascotas */}
                <div className="dashboard-additional-metrics-section">
                    <div className="additional-metric-card">
                        <div className="additional-metric-icon">
                            <FaCar />
                        </div>
                        <div className="additional-metric-content">
                            <p className="additional-metric-label">Vehículos registrados</p>
                            <p className="additional-metric-value">{loading ? '...' : (resumen?.totalVehiculos ?? 0)}</p>
                        </div>
                    </div>

                    <div className="additional-metric-card">
                        <div className="additional-metric-icon">
                            <FaPaw />
                        </div>
                        <div className="additional-metric-content">
                            <p className="additional-metric-label">Casas con mascotas</p>
                            <p className="additional-metric-value">{loading ? '...' : (resumen?.casasConMascotas ?? 0)}</p>
                        </div>
                    </div>
                </div>

                {/* Acciones Rápidas */}
                <div className="dashboard-quick-actions">
                    <h2 className="quick-actions-title">Acciones Rápidas</h2>
                    <div className="quick-actions-grid">
                        <button
                            className="quick-action-btn"
                            onClick={() => navigate('/admin/tickets')}
                        >
                            <div className="quick-action-icon" style={{ backgroundColor: '#3b82f6' }}>
                                <FaFileInvoiceDollar />
                            </div>
                            <span>Registrar Pago</span>
                        </button>

                        <button
                            className="quick-action-btn"
                            onClick={() => navigate('/comunidad/comunicados/nuevo')}
                        >
                            <div className="quick-action-icon" style={{ backgroundColor: '#10b981' }}>
                                <FaBullhorn />
                            </div>
                            <span>Nuevo Comunicado</span>
                        </button>

                        <button
                            className="quick-action-btn"
                            onClick={() => navigate('/comunidad/propuestas')}
                        >
                            <div className="quick-action-icon" style={{ backgroundColor: '#f59e0b' }}>
                                <FaChartLine />
                            </div>
                            <span>Ver Propuestas</span>
                        </button>

                        <button
                            className="quick-action-btn"
                            onClick={() => navigate('/administracion/departamentos/vigilancia')}
                        >
                            <div className="quick-action-icon" style={{ backgroundColor: '#8b5cf6' }}>
                                <FaEye />
                            </div>
                            <span>Vigilancia/Reportes</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Modales */}
            {showNotificationsModal && canViewNotifications && (
                <NotificationsCenterModal
                    fraccionamientoId={fraccionamientoId}
                    categorySet={notificationCategorySet || undefined}
                    allowedActorRoles={allowedActorRoleSet || undefined}
                    currentUserId={userData?.uid || null}
                    onClose={() => setShowNotificationsModal(false)}
                />
            )}
            {isSuperAdmin && (
                <WelcomeConfigModal
                    isOpen={showWelcomeModal}
                    onClose={() => setShowWelcomeModal(false)}
                />
            )}
        </div>
    );
};

export default Dashboard;
